/**
 * Phase 25.4E — Shared Accounting constants.
 *
 * Centralizes every status label, tone class and icon used across the
 * Accounting module. Any new status or label MUST be added here rather
 * than hard-coded in a page/component to prevent terminology drift.
 */
import {
  FileText, Clock, CheckCircle2, XCircle, Ban, Wallet, DollarSign,
  AlertTriangle, RefreshCcw, ShieldAlert,
} from 'lucide-react';

// ─── Payment Batch statuses ─────────────────────────────────────────
export const BATCH_STATUS_META = {
  draft:              { label: 'Draft',              tone: 'bg-slate-100 text-slate-700 border-slate-300', icon: FileText },
  pending_approval:   { label: 'Pending Approval',   tone: 'bg-amber-100 text-amber-800 border-amber-300', icon: Clock },
  approved:           { label: 'Approved',           tone: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2 },
  ready_for_payment:  { label: 'Ready for Payment',  tone: 'bg-blue-100 text-blue-800 border-blue-300', icon: Wallet },
  paid:               { label: 'Paid',               tone: 'bg-emerald-600 text-white border-emerald-700', icon: DollarSign },
  payment_failed:     { label: 'Payment Failed',     tone: 'bg-red-100 text-red-800 border-red-300', icon: AlertTriangle },
  completed_with_exceptions: { label: 'Completed with Exceptions', tone: 'bg-orange-100 text-orange-800 border-orange-300', icon: AlertTriangle },
  rejected:           { label: 'Rejected',           tone: 'bg-red-100 text-red-800 border-red-300', icon: XCircle },
  cancelled:          { label: 'Cancelled',          tone: 'bg-neutral-100 text-neutral-700 border-neutral-300', icon: Ban },
};

// ─── Batch Line (execution) statuses ────────────────────────────────
export const LINE_STATUS_META = {
  pending:        { label: 'Pending',        tone: 'bg-slate-100 text-slate-700 border-slate-300' },
  paid:           { label: 'Paid',           tone: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  failed:         { label: 'Failed',         tone: 'bg-red-100 text-red-800 border-red-300' },
  zero_net:       { label: 'Zero-Net',       tone: 'bg-slate-200 text-slate-800 border-slate-300' },
  partially_paid: { label: 'Partially Paid', tone: 'bg-amber-100 text-amber-800 border-amber-300' },
};

// ─── Statement statuses ─────────────────────────────────────────────
export const STATEMENT_STATUS_META = {
  generated:         { label: 'Generated',         tone: 'bg-slate-100 text-slate-700 border-slate-300' },
  pending_payment:   { label: 'Pending Payment',   tone: 'bg-blue-100 text-blue-800 border-blue-300' },
  paid:              { label: 'Paid',              tone: 'bg-emerald-600 text-white border-emerald-700' },
  fully_applied:     { label: 'Fully Applied',     tone: 'bg-slate-200 text-slate-800 border-slate-300' },
  payment_reversed:  { label: 'Payment Reversed',  tone: 'bg-orange-100 text-orange-800 border-orange-300', icon: RefreshCcw },
  negative_balance:  { label: 'Negative Balance',  tone: 'bg-red-50 text-red-700 border-red-200' },
  voided:            { label: 'Voided',            tone: 'bg-neutral-100 text-neutral-700 border-neutral-300' },
};

// ─── Manual Adjustment statuses ─────────────────────────────────────
export const ADJUSTMENT_STATUS_META = {
  draft:            { label: 'Draft',            tone: 'bg-slate-100 text-slate-700 border-slate-300', icon: FileText },
  pending_approval: { label: 'Pending Approval', tone: 'bg-amber-100 text-amber-800 border-amber-300', icon: Clock },
  posted:           { label: 'Posted',           tone: 'bg-emerald-600 text-white border-emerald-700', icon: CheckCircle2 },
  cancelled:        { label: 'Cancelled',        tone: 'bg-neutral-100 text-neutral-700 border-neutral-300', icon: Ban },
};

// ─── Manual Adjustment types ────────────────────────────────────────
export const ADJUSTMENT_TYPE_META = {
  manual_payment:  { label: 'Payment',  tone: 'bg-red-50 text-red-800 border-red-200' },
  manual_credit:   { label: 'Credit',   tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  manual_debit:    { label: 'Debit',    tone: 'bg-orange-50 text-orange-800 border-orange-200' },
  manual_recovery: { label: 'Recovery', tone: 'bg-blue-50 text-blue-800 border-blue-200' },
};

// ─── Ledger entry-type meta ─────────────────────────────────────────
export const LEDGER_TYPE_META = {
  STATEMENT_POSITIVE:       { label: 'Statement Positive',      tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  STATEMENT_NEGATIVE:       { label: 'Statement Negative',      tone: 'bg-red-50 text-red-700 border-red-200' },
  PAYMENT_ISSUED:           { label: 'Payment Issued',          tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  PAYMENT_REVERSAL:         { label: 'Payment Reversal',        tone: 'bg-orange-50 text-orange-700 border-orange-200' },
  BALANCE_APPLIED_TO_BATCH: { label: 'Balance Applied to Batch',tone: 'bg-slate-50 text-slate-700 border-slate-200' },
  MANUAL_PAYMENT:           { label: 'Manual Payment',          tone: 'bg-red-50 text-red-700 border-red-200' },
  MANUAL_CREDIT:            { label: 'Manual Credit',           tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  MANUAL_DEBIT:             { label: 'Manual Debit',            tone: 'bg-orange-50 text-orange-700 border-orange-200' },
  MANUAL_RECOVERY:          { label: 'Manual Recovery',         tone: 'bg-blue-50 text-blue-700 border-blue-200' },
};

// ─── Recipient type labels (Title Case, singular) ───────────────────
export const RECIPIENT_TYPE_LABEL = {
  agent:     'Agent',
  agency:    'Agency',
  subagency: 'Subagency',
};

// ─── Currency + date formatting helpers ─────────────────────────────
export function money(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return `${n < 0 ? '-$' : '$'}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function moneySigned(v) {
  const n = Number(v || 0);
  if (n > 0) return `+${money(n)}`;
  return money(n);
}

export function fmtDate(iso, style = 'medium') {
  if (!iso) return '—';
  try {
    if (style === 'short') {
      return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
}

// ─── Standardized toast wording ─────────────────────────────────────
export const TOASTS = {
  DRAFT_SAVED:            'Draft Saved',
  SUBMITTED:              'Submitted for Approval',
  APPROVED:               'Approved Successfully',
  REJECTED:               'Rejected',
  CANCELLED:              'Cancelled',
  BATCH_CREATED:          'Batch Created',
  BATCH_MARKED_READY:     'Marked Ready for Payment',
  BATCH_PAID:             'Payment Executed',
  LINE_METHOD_SAVED:      'Payment Method Saved',
  LINE_FAILED:            'Payment Marked as Failed',
  ADJUSTMENT_CREATED:     'Adjustment Draft Created',
  ADJUSTMENT_POSTED:      'Adjustment Posted to Ledger',
  ADJUSTMENT_DELETED:     'Draft Deleted',
  EXPORT_STARTED:         'Preparing Export…',
  EXPORT_FAILED:          'Export Failed',
  ACTION_FAILED:          'Action Failed',
};
